import { AvatarPlaceholder } from '@99designs/common/components/AvatarPlaceholder';
import { getComponentClasses } from '@99designs/common/utils/app';
import { DesignerMeta_User } from '__generated__/DesignerMeta_User';
import Visible from 'common/components/Visible';
import { Color } from 'projects/consts';
import React, { useContext } from 'react';
import Avatar from './Avatar';
import BriefContext from './BriefContext';
import ContactsDrawer from './ContactsDrawer';
import DesignerStack from './DesignerStack';
import { default as Tooltip } from './StoredPopoverCard';

interface TooltipCopy {
    heading: string;
    message: string;
    storageKey: string;
}

interface Props {
    onRemoveDesigner: (index: number) => void;
    color?: Color;
    selectDesignersCopy: TooltipCopy;
    confirmDesignersCopy: TooltipCopy;
    designers: { id: number; displayName: string }[];
    showTooltips?: boolean;
    brief: {
        id: string;
        designers: DesignerMeta_User[];
        contacts: DesignerMeta_User[];
    };
    isDesignerStep?: boolean;
    disableInteraction?: boolean;
}

const DesignerSelection: React.SFC<Props> = ({
    designers,
    onRemoveDesigner,
    color,
    selectDesignersCopy,
    confirmDesignersCopy,
    showTooltips = false,
    brief,
    isDesignerStep,
    disableInteraction,
}) => {
    const { disableContacts, totalAllowableDesigners } = useContext(BriefContext);
    const showContacts = brief.contacts.length !== 0 && !disableInteraction && !disableContacts;

    return (
        <div className="designer-selection">
            <Visible sizes={['small', 'medium']}>
                <DesignerStack brief={brief} disableInteraction={disableInteraction} />
            </Visible>

            <Visible sizes={['normal', 'large']}>
                <div className="designer-selection__item-list">
                    {designers.map((designer, idx) => (
                        <div key={designer.id} className="designer-selection__item">
                            {showTooltips && idx === totalAllowableDesigners - 1 ? (
                                <Tooltip
                                    heading={confirmDesignersCopy.heading}
                                    message={confirmDesignersCopy.message}
                                    storageKey={confirmDesignersCopy.storageKey}
                                    storageSource={localStorage}
                                    color={color}
                                >
                                    <Avatar
                                        userId={designer.id}
                                        onRemove={
                                            !disableInteraction ? onRemoveDesigner : undefined
                                        }
                                    />
                                </Tooltip>
                            ) : (
                                <Avatar
                                    userId={designer.id}
                                    onRemove={!disableInteraction ? onRemoveDesigner : undefined}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </Visible>

            <Visible
                className={getComponentClasses(
                    'designer-section__action',
                    designers.length > 0 ? ['with-stack'] : []
                )}
                sizes={['small', 'medium']}
            >
                {brief.contacts.length !== 0 && <ContactsDrawer brief={brief} />}
            </Visible>

            <Visible className="designer-section__action" sizes={['normal', 'large']}>
                {showContacts ? (
                    <ContactsDrawer brief={brief} />
                ) : (
                    isDesignerStep &&
                    (designers.length < totalAllowableDesigners && (
                        <div key="placeholder" className="designer-selection__item">
                            {showTooltips ? (
                                <Tooltip
                                    heading={selectDesignersCopy.heading}
                                    message={selectDesignersCopy.message}
                                    storageKey={selectDesignersCopy.storageKey}
                                    storageSource={localStorage}
                                    color={color}
                                >
                                    <AvatarPlaceholder title={__('Select another designer.')} />
                                </Tooltip>
                            ) : (
                                <AvatarPlaceholder title={__('Select another designer.')} />
                            )}
                        </div>
                    ))
                )}
            </Visible>
        </div>
    );
};

export default DesignerSelection;
